import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useRefreshMutation } from './authApiSlice';
import { selectCurrentToken } from './authSlice';
import jwtDecode from 'jwt-decode';

/**
 * Hook to set up proactive token refresh with fallback mechanisms
 * This will refresh the access token periodically before it expires
 */
export const useTokenRefresh = () => {
  const dispatch = useDispatch();
  const token = useSelector(selectCurrentToken);
  const [refresh] = useRefreshMutation();
  
  // Helper to check if a session should still be active
  const checkSessionStatus = () => {
    // Check for the session_active cookie first (our new indicator)
    const hasSessionCookie = document.cookie.includes('session_active=true');
    
    // Also check localStorage backup (implemented below)
    const sessionBackup = localStorage.getItem('session_backup');
    const hasValidBackup = sessionBackup && (parseInt(sessionBackup) > Date.now());
    
    return hasSessionCookie || hasValidBackup;
  };
  
  // Helper to decode and check token expiration
  const isTokenExpiringSoon = (token) => {
    if (!token) return false;
    try {
      const decoded = jwtDecode(token);
      const expTime = decoded.exp * 1000; // convert to ms
      return expTime < Date.now() + 15 * 60 * 1000; // 15 min buffer
    } catch (e) {
      console.error('Token decode error:', e);
      return true; // Assume it needs refresh if we can't decode
    }
  };
  
  useEffect(() => {
    // Backup session status in localStorage as fallback
    // This gives us a backup mechanism if cookies are cleared/blocked
    if (token) {
      try {
        const decoded = jwtDecode(token);
        const expTime = decoded.exp * 1000;
        // Store expiry time + 7 days (match refresh token lifetime)
        localStorage.setItem('session_backup', String(expTime + 14 * 24 * 60 * 60 * 1000));
      } catch (e) {
        console.error('Session backup creation error:', e);
      }
    }
  }, [token]);
  
  useEffect(() => {
    // Session recovery logic
    const attemptSessionRecovery = async () => {
      if (!token && checkSessionStatus()) {
        // We should have a session but no token - try refresh
        console.log('Attempting session recovery...');
        try {
          await refresh();
        } catch (err) {
          console.error('Session recovery failed:', err);
          // Clear backup if recovery fails
          localStorage.removeItem('session_backup');
        }
      }
    };
    
    // Try to recover on mount
    attemptSessionRecovery();
  }, [refresh, token]);
  
  useEffect(() => {
    // Regular refresh interval (reduced to 45 minutes for safety)
    const REFRESH_INTERVAL = 45 * 60 * 1000;
    
    // Only try immediate refresh if we have a token that's expiring soon
    if (token && isTokenExpiringSoon(token)) {
      refresh().catch(err => {
        console.log('Initial token refresh failed:', err);
      });
    }
    
    // Set up periodic token refresh with error recovery
    const refreshInterval = setInterval(() => {
      // Only attempt refresh if we have a token or should have a session
      if (token || checkSessionStatus()) {
        refresh().catch(err => {
          console.log('Scheduled token refresh failed:', err);
        });
      }
    }, REFRESH_INTERVAL);
    
    return () => clearInterval(refreshInterval);
  }, [refresh, token]);
};

/**
 * Component to handle token refresh
 * Add this to your App.js or layout component
 */
export const TokenRefreshManager = () => {
  useTokenRefresh();
  return null;
};
